<script setup>
import { onMounted } from 'vue'
import { useSeo } from '@/composables/useSeo'
import BrowserFrame from '@/components/platform/BrowserFrame.vue'
import HalftoneVeil from '@/components/HalftoneVeil.vue'
import ScanMock from '@/components/platform/ScanMock.vue'
import DigestMock from '@/components/platform/DigestMock.vue'
import AutomationMock from '@/components/platform/AutomationMock.vue'
import OverviewMock from '@/components/platform/OverviewMock.vue'
import ActivitiesMock from '@/components/platform/ActivitiesMock.vue'
import ReportsMock from '@/components/platform/ReportsMock.vue'
import AccountsMock from '@/components/platform/AccountsMock.vue'
import { CheckIcon } from '@heroicons/vue/24/outline'

// Real assets. Product UI is rendered by the vector mocks above until
// the scripted retina captures land (npm run shots) — swap back here.
import landingShot from '@/assets/images/homepage/platform-registration.webp'
import studioShot from '@/assets/images/homepage/home_branding_wide.png'

useSeo('platform')

/**
 * Platform — the product page, organized as the event lifecycle:
 * before (registration, studio) → during (check-in, program, live ops)
 * → after (Heimdall digests, reports), plus the cross-cutting
 * automation engine and the platform foundations.
 *
 * Every section answers one concrete organizer question (the eyebrow),
 * shows the product (screenshot or faithful UI mockup) and, on the
 * three pivotal moments, spells out before → with Bifrost → result.
 * All copy lives in i18n under `platform.*`.
 */

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal')
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.08 }
  )
  document.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el))
})
</script>

<template>
  <!-- ================= HERO ================= -->
  <header class="bg-slate-950 pt-36 lg:pt-44 overflow-hidden">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mx-auto text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
        <div class="inline-flex items-center px-3 py-1.5 border border-white/15 text-slate-300 text-xs font-mono tracking-wide mb-8 rounded-md">
          <span class="flex h-1.5 w-1.5 rounded-full bg-primary-400 me-2"></span>
          {{ $t('platform.hero.badge') }}
        </div>

        <h1 class="text-4xl md:text-6xl font-semibold text-white leading-[1.08] rtl:leading-[1.35]">
          {{ $t('platform.hero.title') }}
          <span class="block text-primary-400 mt-2">{{ $t('platform.hero.accent') }}</span>
        </h1>

        <p class="mt-7 max-w-2xl mx-auto text-lg md:text-xl text-slate-400 leading-relaxed">
          {{ $t('platform.hero.desc') }}
        </p>

        <div class="mt-9 flex flex-col sm:flex-row justify-center gap-3">
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 bg-white text-slate-950 rounded-md font-semibold text-base hover:bg-slate-200 transition">
            {{ $t('platform.hero.ctaDemo') }}
          </a>
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 border border-white/20 text-white rounded-md font-semibold text-base hover:bg-white/10 transition">
            {{ $t('platform.hero.ctaSecondary') }}
          </a>
        </div>

        <div class="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[13px] text-slate-500">
          <span v-for="i in 3" :key="i" class="inline-flex items-center gap-2">
            <span class="w-1 h-1 rounded-full bg-primary-400/70"></span>
            {{ $t(`platform.hero.chips[${i - 1}]`) }}
          </span>
        </div>
      </div>

      <!-- the product, immediately -->
      <div class="mt-14 lg:mt-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out delay-150">
        <BrowserFrame url="app.bifrost.events/events/tech-days/overview">
          <div role="img" :aria-label="$t('platform.alts.hero')">
            <OverviewMock variant="full" />
          </div>
        </BrowserFrame>
      </div>
    </div>
    <!-- Raccord bas du mock produit : trame demi-teinte vers le blanc -->
    <HalftoneVeil
      color="#ffffff"
      side="bottom"
      class="h-20 lg:h-28 -mt-24 lg:-mt-32 relative"
    />
  </header>

  <!-- ================= POSITIONING ================= -->
  <section class="bg-white pt-10 pb-20 lg:pb-24">
    <div class="max-w-3xl mx-auto px-4 sm:px-6 text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
      <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('platform.position.label') }}</p>
      <h2 class="text-3xl md:text-4xl font-semibold text-slate-900 mb-5">{{ $t('platform.position.title') }}</h2>
      <p class="text-lg text-slate-500 leading-relaxed">{{ $t('platform.position.desc') }}</p>
    </div>

    <!-- lifecycle: the page's table of contents -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
      <div class="grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div v-for="p in 3" :key="p" class="bg-white p-7 lg:p-8">
          <p class="text-[11px] font-mono text-primary-600 tracking-widest uppercase mb-2">
            {{ $t(`platform.lifecycle.phases[${p - 1}].tag`) }}
          </p>
          <h3 class="text-lg font-semibold text-slate-900 mb-4">{{ $t(`platform.lifecycle.phases[${p - 1}].title`) }}</h3>
          <ul class="space-y-2.5">
            <li v-for="j in 4" :key="j" class="flex items-start text-sm text-slate-600">
              <CheckIcon class="w-4 h-4 mt-0.5 me-2.5 flex-shrink-0 text-primary-600" />
              {{ $t(`platform.lifecycle.phases[${p - 1}].items[${j - 1}]`) }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= 01 · REGISTRATION ================= -->
  <section class="py-20 lg:py-28 bg-slate-50 border-t border-slate-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-3">{{ $t('platform.reg.phase') }}</p>
          <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('platform.reg.q') }}</p>
          <h2 class="text-3xl md:text-4xl font-semibold text-slate-900 mb-5">{{ $t('platform.reg.title') }}</h2>
          <p class="text-lg text-slate-500 leading-relaxed mb-8">{{ $t('platform.reg.desc') }}</p>
          <ul class="space-y-3">
            <li v-for="j in 4" :key="j" class="flex items-start text-[15px] text-slate-700">
              <CheckIcon class="w-4.5 h-4.5 mt-0.5 me-3 flex-shrink-0 text-primary-600" />
              {{ $t(`platform.reg.points[${j - 1}]`) }}
            </li>
          </ul>
        </div>
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-100">
          <BrowserFrame url="attendee.bifrost.events/e/night-run">
            <img :src="landingShot" :alt="$t('platform.alts.reg')" class="w-full block" loading="lazy" />
          </BrowserFrame>
        </div>
      </div>

      <!-- before → with → result -->
      <div class="mt-14 grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div class="bg-white p-6">
          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-2.5">{{ $t('platform.bwr.before') }}</p>
          <p class="text-sm text-slate-500 leading-relaxed">{{ $t('platform.reg.bwr.before') }}</p>
        </div>
        <div class="bg-white p-6">
          <p class="text-[11px] font-mono text-primary-600 tracking-widest uppercase mb-2.5">{{ $t('platform.bwr.with') }}</p>
          <p class="text-sm text-slate-700 leading-relaxed">{{ $t('platform.reg.bwr.with') }}</p>
        </div>
        <div class="bg-slate-950 p-6">
          <p class="text-[11px] font-mono text-primary-400 tracking-widest uppercase mb-2.5">{{ $t('platform.bwr.result') }}</p>
          <p class="text-sm text-slate-200 leading-relaxed">{{ $t('platform.reg.bwr.result') }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= 01 · STUDIO ================= -->
  <section class="py-20 lg:py-28 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div class="lg:order-2 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-3">{{ $t('platform.studio.phase') }}</p>
          <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('platform.studio.q') }}</p>
          <h2 class="text-3xl md:text-4xl font-semibold text-slate-900 mb-5">{{ $t('platform.studio.title') }}</h2>
          <p class="text-lg text-slate-500 leading-relaxed mb-8">{{ $t('platform.studio.desc') }}</p>
          <ul class="space-y-3">
            <li v-for="j in 4" :key="j" class="flex items-start text-[15px] text-slate-700">
              <CheckIcon class="w-4.5 h-4.5 mt-0.5 me-3 flex-shrink-0 text-primary-600" />
              {{ $t(`platform.studio.points[${j - 1}]`) }}
            </li>
          </ul>
        </div>
        <div class="lg:order-1 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-100">
          <img :src="studioShot" :alt="$t('platform.alts.studio')"
            class="w-full rounded-xl border border-slate-200 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)]" loading="lazy" />
        </div>
      </div>
    </div>
  </section>

  <!-- ================= 02 · CHECK-IN ================= -->
  <section class="py-20 lg:py-28 bg-slate-50 border-t border-slate-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-3">{{ $t('platform.checkin.phase') }}</p>
          <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('platform.checkin.q') }}</p>
          <h2 class="text-3xl md:text-4xl font-semibold text-slate-900 mb-5">{{ $t('platform.checkin.title') }}</h2>
          <p class="text-lg text-slate-500 leading-relaxed mb-8">{{ $t('platform.checkin.desc') }}</p>
          <ul class="space-y-3">
            <li v-for="j in 4" :key="j" class="flex items-start text-[15px] text-slate-700">
              <CheckIcon class="w-4.5 h-4.5 mt-0.5 me-3 flex-shrink-0 text-primary-600" />
              {{ $t(`platform.checkin.points[${j - 1}]`) }}
            </li>
          </ul>
        </div>
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-100">
          <ScanMock />
        </div>
      </div>

      <div class="mt-14 grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div class="bg-white p-6">
          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-2.5">{{ $t('platform.bwr.before') }}</p>
          <p class="text-sm text-slate-500 leading-relaxed">{{ $t('platform.checkin.bwr.before') }}</p>
        </div>
        <div class="bg-white p-6">
          <p class="text-[11px] font-mono text-primary-600 tracking-widest uppercase mb-2.5">{{ $t('platform.bwr.with') }}</p>
          <p class="text-sm text-slate-700 leading-relaxed">{{ $t('platform.checkin.bwr.with') }}</p>
        </div>
        <div class="bg-slate-950 p-6">
          <p class="text-[11px] font-mono text-primary-400 tracking-widest uppercase mb-2.5">{{ $t('platform.bwr.result') }}</p>
          <p class="text-sm text-slate-200 leading-relaxed">{{ $t('platform.checkin.bwr.result') }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= 02 · PROGRAM & ACTIVITIES ================= -->
  <section class="py-20 lg:py-28 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div class="lg:order-2 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-3">{{ $t('platform.program.phase') }}</p>
          <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('platform.program.q') }}</p>
          <h2 class="text-3xl md:text-4xl font-semibold text-slate-900 mb-5">{{ $t('platform.program.title') }}</h2>
          <p class="text-lg text-slate-500 leading-relaxed mb-8">{{ $t('platform.program.desc') }}</p>
          <ul class="space-y-3">
            <li v-for="j in 4" :key="j" class="flex items-start text-[15px] text-slate-700">
              <CheckIcon class="w-4.5 h-4.5 mt-0.5 me-3 flex-shrink-0 text-primary-600" />
              {{ $t(`platform.program.points[${j - 1}]`) }}
            </li>
          </ul>
        </div>
        <div class="lg:order-1 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-100">
          <BrowserFrame url="app.bifrost.events/events/tech-days/activities">
            <div role="img" :aria-label="$t('platform.alts.program')">
              <ActivitiesMock />
            </div>
          </BrowserFrame>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= 02 · LIVE OPS (dark) ================= -->
  <section class="py-20 lg:py-28 bg-slate-950 text-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-[11px] font-mono text-slate-500 tracking-widest uppercase mb-3">{{ $t('platform.live.phase') }}</p>
          <p class="text-xs font-mono text-primary-400 tracking-widest uppercase mb-4">{{ $t('platform.live.q') }}</p>
          <h2 class="text-3xl md:text-4xl font-semibold mb-5">{{ $t('platform.live.title') }}</h2>
          <p class="text-lg text-slate-400 leading-relaxed mb-8">{{ $t('platform.live.desc') }}</p>
          <ul class="space-y-3">
            <li v-for="j in 3" :key="j" class="flex items-start text-[15px] text-slate-300">
              <CheckIcon class="w-4.5 h-4.5 mt-0.5 me-3 flex-shrink-0 text-primary-400" />
              {{ $t(`platform.live.points[${j - 1}]`) }}
            </li>
          </ul>
        </div>
        <!-- dashboard close-up: the KPI band of the live overview -->
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-100">
          <div class="rounded-xl border border-white/10 overflow-hidden shadow-[0_24px_70px_-24px_rgba(0,0,0,0.8)]"
            role="img" :aria-label="$t('platform.alts.hero')">
            <OverviewMock variant="kpis" />
          </div>
          <p class="mt-3 text-[12px] text-slate-500 text-center font-mono">{{ $t('platform.live.caption') }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= 03 · HEIMDALL ================= -->
  <section class="py-20 lg:py-28 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <div class="inline-flex items-center gap-3 mb-6 px-4 py-2 rounded-full border border-slate-200 bg-slate-50">
            <svg viewBox="0 0 40 24" fill="none" class="w-8 h-5" aria-hidden="true">
              <path d="M3 12 Q20 1 37 12 Q20 23 3 12 Z" stroke="#7c5ce0" stroke-width="1.6" />
              <circle cx="20" cy="12" r="5.5" stroke="#b04fd6" stroke-width="1.2" />
              <circle cx="20" cy="12" r="1.8" fill="#e8a34d" />
            </svg>
            <span class="font-heading font-semibold text-sm text-slate-900">{{ $t('platform.heimdall.badgeName') }}</span>
            <span class="w-px h-4 bg-slate-200" aria-hidden="true"></span>
            <span class="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{{ $t('platform.heimdall.badgeTag') }}</span>
          </div>

          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-3">{{ $t('platform.heimdall.phase') }}</p>
          <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('platform.heimdall.q') }}</p>
          <h2 class="text-3xl md:text-4xl font-semibold text-slate-900 mb-5">{{ $t('platform.heimdall.title') }}</h2>
          <p class="text-lg text-slate-500 leading-relaxed mb-5">{{ $t('platform.heimdall.desc') }}</p>
          <p class="text-lg text-slate-500 leading-relaxed mb-8">{{ $t('platform.heimdall.desc2') }}</p>

          <ul class="space-y-3 mb-8">
            <li v-for="j in 4" :key="j" class="flex items-start text-[15px] text-slate-700">
              <CheckIcon class="w-4.5 h-4.5 mt-0.5 me-3 flex-shrink-0 text-primary-600" />
              {{ $t(`platform.heimdall.points[${j - 1}]`) }}
            </li>
          </ul>

          <div class="border-s-2 border-primary-600 ps-4 py-1">
            <p class="text-[15px] text-slate-700 font-medium">{{ $t('platform.heimdall.note') }}</p>
          </div>
        </div>

        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-100">
          <div class="max-w-md mx-auto">
            <DigestMock />
          </div>
        </div>
      </div>

      <div class="mt-14 grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div class="bg-white p-6">
          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-2.5">{{ $t('platform.bwr.before') }}</p>
          <p class="text-sm text-slate-500 leading-relaxed">{{ $t('platform.heimdall.bwr.before') }}</p>
        </div>
        <div class="bg-white p-6">
          <p class="text-[11px] font-mono text-primary-600 tracking-widest uppercase mb-2.5">{{ $t('platform.bwr.with') }}</p>
          <p class="text-sm text-slate-700 leading-relaxed">{{ $t('platform.heimdall.bwr.with') }}</p>
        </div>
        <div class="bg-slate-950 p-6">
          <p class="text-[11px] font-mono text-primary-400 tracking-widest uppercase mb-2.5">{{ $t('platform.bwr.result') }}</p>
          <p class="text-sm text-slate-200 leading-relaxed">{{ $t('platform.heimdall.bwr.result') }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= 03 · REPORTS ================= -->
  <section class="py-20 lg:py-28 bg-slate-50 border-t border-slate-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div class="lg:order-2 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-3">{{ $t('platform.reports.phase') }}</p>
          <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('platform.reports.q') }}</p>
          <h2 class="text-3xl md:text-4xl font-semibold text-slate-900 mb-5">{{ $t('platform.reports.title') }}</h2>
          <p class="text-lg text-slate-500 leading-relaxed mb-8">{{ $t('platform.reports.desc') }}</p>
          <ul class="space-y-3">
            <li v-for="j in 4" :key="j" class="flex items-start text-[15px] text-slate-700">
              <CheckIcon class="w-4.5 h-4.5 mt-0.5 me-3 flex-shrink-0 text-primary-600" />
              {{ $t(`platform.reports.points[${j - 1}]`) }}
            </li>
          </ul>
        </div>
        <div class="lg:order-1 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-100">
          <BrowserFrame url="app.bifrost.events/events/tech-days/reports">
            <div role="img" :aria-label="$t('platform.alts.reports')">
              <ReportsMock />
            </div>
          </BrowserFrame>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= AUTOMATION ================= -->
  <section class="py-20 lg:py-28 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-[11px] font-mono text-slate-400 tracking-widest uppercase mb-3">{{ $t('platform.automation.phase') }}</p>
          <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('platform.automation.q') }}</p>
          <h2 class="text-3xl md:text-4xl font-semibold text-slate-900 mb-5">{{ $t('platform.automation.title') }}</h2>
          <p class="text-lg text-slate-500 leading-relaxed mb-8">{{ $t('platform.automation.desc') }}</p>
          <ul class="space-y-3">
            <li v-for="j in 3" :key="j" class="flex items-start text-[15px] text-slate-700">
              <CheckIcon class="w-4.5 h-4.5 mt-0.5 me-3 flex-shrink-0 text-primary-600" />
              {{ $t(`platform.automation.points[${j - 1}]`) }}
            </li>
          </ul>
        </div>
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-100">
          <div class="max-w-md mx-auto">
            <AutomationMock />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= FOUNDATIONS (dark) ================= -->
  <section class="py-20 lg:py-28 bg-slate-950 text-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-14 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-400 tracking-widest uppercase mb-4">{{ $t('platform.foundations.label') }}</p>
        <h2 class="text-3xl md:text-4xl font-semibold mb-5">{{ $t('platform.foundations.title') }}</h2>
        <p class="text-lg text-slate-400">{{ $t('platform.foundations.desc') }}</p>
      </div>

      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div class="grid sm:grid-cols-2 gap-x-8 gap-y-9 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <div v-for="i in 6" :key="i">
            <h3 class="text-base font-semibold text-white mb-2">{{ $t(`platform.foundations.facts[${i - 1}].title`) }}</h3>
            <p class="text-sm text-slate-400 leading-relaxed">{{ $t(`platform.foundations.facts[${i - 1}].desc`) }}</p>
          </div>
        </div>
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-100">
          <div class="rounded-xl border border-white/10 overflow-hidden shadow-[0_24px_70px_-24px_rgba(0,0,0,0.8)]"
            role="img" :aria-label="$t('platform.alts.accounts')">
            <AccountsMock />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= CTA ================= -->
  <section class="py-24 bg-white">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="bg-slate-950 rounded-xl p-12 md:p-20 text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <h2 class="text-3xl md:text-5xl font-semibold text-white mb-6">{{ $t('platform.cta.title') }}</h2>
        <p class="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">{{ $t('platform.cta.desc') }}</p>
        <div class="flex flex-col sm:flex-row justify-center gap-3">
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 bg-white text-slate-950 font-semibold rounded-md text-base hover:bg-slate-200 transition">
            {{ $t('platform.cta.demo') }}
          </a>
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 border border-white/20 text-white font-semibold rounded-md text-base hover:bg-white/10 transition">
            {{ $t('platform.cta.contact') }}
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
</style>
